import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

interface LocaleContent {
  lang: string;
  heading: string;
  subheading: string;
  body: string;
  close: string;
  portalLink: string;
  portalLinkHref: string;
}

const CONTENT: Record<string, LocaleContent> = {
  'da-DK': {
    lang: 'da',
    heading: 'Tak for dine svar',
    subheading: 'Vi har modtaget dine svar.',
    body: 'Din klinik vil gennemgå dine besvarelser snarest muligt. Du behøver ikke foretage dig yderligere.',
    close: 'Du kan nu lukke dette vindue.',
    portalLink: 'Gå til min side',
    portalLinkHref: '/portal/questionnaires',
  },
  'en-DK': {
    lang: 'en',
    heading: 'Thank you for your answers',
    subheading: 'We have received your responses.',
    body: 'Your clinic will review your answers as soon as possible. You do not need to do anything else.',
    close: 'You can now close this window.',
    portalLink: 'Go to my page',
    portalLinkHref: '/portal/questionnaires',
  },
};

/**
 * FORMS-5: Thank-you page shown after a patient submits a questionnaire in Forms.
 * Forms redirects to /spoergeskemaer/tak?token=<access_token>&locale=<da-DK|en-DK>.
 * Locale is read from ?locale query param; falls back to da-DK.
 * Static copy — no network calls required.
 * lang attribute on <html> is set via document.documentElement.lang for a11y.
 * Lighthouse a11y target: ≥ 95.
 */
export function QuestionnaireThankYou() {
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale') ?? 'da-DK';
  const content = CONTENT[locale] ?? CONTENT['da-DK'];

  useEffect(() => {
    const prev = document.documentElement.lang;
    document.documentElement.lang = content.lang;
    return () => {
      document.documentElement.lang = prev;
    };
  }, [content.lang]);

  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-12 dark:bg-gray-900"
      aria-labelledby="thank-you-heading"
    >
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div
          aria-hidden="true"
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30"
        >
          <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
        </div>

        <h1
          id="thank-you-heading"
          className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900 dark:text-white"
        >
          {content.heading}
        </h1>

        <p className="mt-2 text-center text-base font-medium text-gray-700 dark:text-gray-300">
          {content.subheading}
        </p>

        <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          {content.body}
        </p>

        <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
          {content.close}
        </p>

        <nav aria-label={content.portalLink} className="mt-8 text-center">
          <a
            href={content.portalLinkHref}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            {content.portalLink}
          </a>
        </nav>
      </div>
    </main>
  );
}
